import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";

import { RootState } from "../../redux/store";
import { LocaleState, Currency, Country } from "./types/locale-types";

const API_BASE = process.env.REACT_APP_API_URL || "";

async function handleResp(res: Response) {
  const text = await res.text();
  let payload: any = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = text;
  }
  if (!res.ok) {
    const msg =
      (payload && (payload.message || payload.error)) ||
      res.statusText ||
      "Request failed";
    throw new Error(msg);
  }
  return payload;
}

export const fetchUserCountry = createAsyncThunk<
  { country: string; countryCode: string },
  void,
  { rejectValue: string; state: RootState }
>("locale/fetchUserCountry", async (_, { rejectWithValue, getState }) => {
  const state = getState() as RootState;
  const token = (state as any).auth?.token;

  // Log if unauthorized and return rejected immediately
  if (!token) {
    console.error("Fetch Countries Failed: Unauthorized (Token Missing)");
    return rejectWithValue("Unauthorized");
  }

  try {
    const url = new URL(`${API_BASE}/user-country`);
    const res = await fetch(url.toString(), {
      headers: { Authorization: `Bearer ${token}` },
    });
    const payload: any = await handleResp(res);
    // Extract data logic
    return payload;
  } catch (err: any) {
    // Log error when network or parsing fails
    console.error("Fetch Countries Network/Parsing Error:", err.message);
    return rejectWithValue(err?.message ?? "Failed to fetch countries");
  }
});

export const fetchCountries = createAsyncThunk<
  Country[],
  { page?: number; perPage?: number; search?: string } | undefined,
  { rejectValue: string; state: RootState }
>(
  "locale/fetchCountries",
  async (params = { page: 1, perPage: 50 }, { rejectWithValue, getState }) => {
    const state = getState() as RootState;
    const token = (state as any).auth?.token;

    // Log if unauthorized and return rejected immediately
    if (!token) {
      console.error("Fetch Countries Failed: Unauthorized (Token Missing)");
      return rejectWithValue("Unauthorized");
    }

    try {
      const url = new URL(`${API_BASE}/countries`);
      url.searchParams.set("page", String(params.page ?? 1));
      url.searchParams.set("per_page", String(params.perPage ?? 50));
      if (params.search) url.searchParams.set("search", params.search);

      const res = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload: any = await handleResp(res);

      // Extract data logic
      if (Array.isArray(payload)) return payload;
      if (payload?.data?.data && Array.isArray(payload.data.data))
        return payload.data.data;
      if (payload?.data && Array.isArray(payload.data)) return payload.data;
      return [];
    } catch (err: any) {
      // Log error when network or parsing fails
      console.error("Fetch Countries Network/Parsing Error:", err.message);
      return rejectWithValue(err?.message ?? "Failed to fetch countries");
    }
  }
);

export const fetchCurrencies = createAsyncThunk<
  Currency[],
  { page?: number; perPage?: number; search?: string } | undefined,
  { rejectValue: string; state: RootState }
>(
  "locale/fetchCurrencies",
  async (params = { page: 1, perPage: 50 }, { rejectWithValue, getState }) => {
    const state = getState() as RootState;
    const token = (state as any).auth?.token;

    // Log if unauthorized and return rejected immediately
    if (!token) {
      console.error("Fetch Currencies Failed: Unauthorized (Token Missing)");
      return rejectWithValue("Unauthorized");
    }

    try {
      const url = new URL(`${API_BASE}/currencies`);
      url.searchParams.set("page", String(params.page ?? 1));
      url.searchParams.set("per_page", String(params.perPage ?? 50));
      if (params.search) url.searchParams.set("search", params.search);

      const res = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload: any = await handleResp(res);

      // Extract data logic
      if (Array.isArray(payload)) return payload;
      if (payload?.data?.data && Array.isArray(payload.data.data))
        return payload.data.data;
      if (payload?.data && Array.isArray(payload.data)) return payload.data;
      return [];
    } catch (err: any) {
      // Log error when network or parsing fails
      console.error("Fetch Currencies Network/Parsing Error:", err.message);
      return rejectWithValue(err?.message ?? "Failed to fetch currencies");
    }
  }
);

// Helper function to find and set country (internal to this file)
const updateSelectedCountryFromLocation = (state: LocaleState) => {
  const data = state.userLocation.data;
  const items = state.country.items;

  if (data && items.length > 0) {
    const matchedCountry = items.find(
      (c) => c.symbol.toLowerCase() === data.countryCode.toLowerCase()
    );

    if (matchedCountry) {
      state.selectedCountry = matchedCountry;
      if (matchedCountry.currency) {
        state.selectedCurrency = matchedCountry.currency;
      }
    }
  }
};

const initialState: LocaleState = {
  country: {
    items: [],
    loading: false,
    error: null,
  },
  currency: {
    items: [],
    loading: false,
    error: null,
  },
  selectedCountry: {
    id: 3,
    name: "Others",
    symbol: "USA",
    default_currency_id: 3,
    created_at: "2025-12-08T13:39:19.000000Z",
    updated_at: "2025-12-09T08:57:30.000000Z",
    currency: {
      id: 3,
      name: "US Dollar (USD)",
      symbol: "$",
      exchange_rate: 1.0,
      created_at: "2025-12-08T13:31:50.000000Z",
      updated_at: "2025-12-08T13:31:50.000000Z",
    },
  },
  selectedCurrency: {
    id: 3,
    name: "US Dollar (USD)",
    symbol: "$",
    exchange_rate: 1.0,
    created_at: "2025-12-08T13:31:50.000000Z",
    updated_at: "2025-12-08T13:31:50.000000Z",
  },
  userLocation: {
    data: null,
    loading: false,
    error: null,
  },
};

const localeSlice = createSlice({
  name: "locale",
  initialState,
  reducers: {
    clearLocaleCountryError(state) {
      state.country.error = null;
    },
    clearLocaleCurrencyError(state) {
      state.currency.error = null;
    },
    setCurrency(state, action: PayloadAction<Currency>) {
      state.selectedCurrency = action.payload;
    },
    setCountry(state, action: PayloadAction<Country>) {
      state.selectedCountry = action.payload;
      if (action.payload.currency) {
        state.selectedCurrency = action.payload.currency;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCurrencies.pending, (state) => {
        state.currency.loading = true;
        state.currency.error = null;
      })
      .addCase(fetchCurrencies.fulfilled, (state, action) => {
        state.currency.loading = false;
        state.currency.items = action.payload;
      })
      .addCase(fetchCurrencies.rejected, (state, action) => {
        state.currency.loading = false;
        state.currency.error =
          (action.payload as string) || "Failed to fetch currencies";
      })
      .addCase(fetchCountries.pending, (state) => {
        state.country.loading = true;
        state.country.error = null;
      })
      .addCase(fetchCountries.fulfilled, (state, action) => {
        state.country.loading = false;
        state.country.items = action.payload;
        updateSelectedCountryFromLocation(state);
      })
      .addCase(fetchCountries.rejected, (state, action) => {
        state.country.loading = false;
        state.country.error =
          (action.payload as string) || "Failed to fetch countries";
      })
      .addCase(fetchUserCountry.pending, (state) => {
        state.userLocation.loading = true;
        state.userLocation.error = null;
      })
      .addCase(fetchUserCountry.fulfilled, (state, action) => {
        state.userLocation.loading = false;
        state.userLocation.data = action.payload;
        updateSelectedCountryFromLocation(state);
      })
      .addCase(fetchUserCountry.rejected, (state, action) => {
        state.userLocation.loading = false;
        state.userLocation.error =
          (action.payload as string) || "Failed to get user country";
      });
  },
});

export const {
  clearLocaleCountryError,
  clearLocaleCurrencyError,
  setCountry,
  setCurrency,
} = localeSlice.actions;
export default localeSlice.reducer;
